import { api } from "../context/AuthContext";
import React, { useState, useEffect } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Box,
  Divider,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import {
  Person,
  Subscriptions,
  Payment,
  SupportAgent,
  History,
  Work,
  BookmarkBorder,
  Group,
  AdminPanelSettings,
  Dashboard,
  ConfirmationNumber,
  Assignment,
  People,
  Drafts,
  Category,
  VideoCall,
  VideoLibrary,
  RecordVoiceOver,
  WhatsApp,
  Phone,
  ReceiptLong,
  Assessment,
  Campaign,
  EventAvailable,
} from "@mui/icons-material";
import { BarChart3 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Design tokens — sidebar uses the exact same dark navy as the header
const SB = {
  bg:           '#0A0F2C',
  activeBg:     '#2563EB',
  activeGlow:   '0 4px 18px rgba(37,99,235,0.38)',
  hoverBg:      'rgba(255,255,255,0.07)',
  textDefault:  'rgba(255,255,255,0.68)',
  textActive:   '#ffffff',
  textSection:  'rgba(255,255,255,0.32)',
  iconDefault:  'rgba(255,255,255,0.45)',
  iconActive:   '#ffffff',
  iconHover:    'rgba(255,255,255,0.9)',
  divider:      'rgba(255,255,255,0.07)',
  cardBg:       'rgba(255,255,255,0.05)',
  cardBorder:   'rgba(255,255,255,0.09)',
  statBg:       'rgba(255,255,255,0.07)',
  scrollThumb:  'rgba(255,255,255,0.12)',
};

const menuConfig = {
  super_admin: [
    {
      section: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, path: "/super-admin/dashboard" },
      ],
    },
    {
      section: "Management",
      items: [
        { text: "Users", icon: <People sx={{ fontSize: 20 }} />, path: "/super-admin/user-history" },
        { text: "Recruiters Requests", icon: <AdminPanelSettings sx={{ fontSize: 20 }} />, path: "/super-admin/recruiters-requests" },
        { text: "All Recruiter History", icon: <History sx={{ fontSize: 20 }} />, path: "/super-admin/admin-history" },
        { text: "Analytics", icon: <BarChart3 size={20} />, path: "/super-admin/analytics" },
      ],
    },
    {
      section: "Operations",
      items: [
        { text: "Jobs", icon: <Work sx={{ fontSize: 20 }} />, path: "/super-admin/jobs-history" },
        { text: "Interviews", icon: <History sx={{ fontSize: 20 }} />, path: "/super-admin/interview-history" },
        { text: "Scheduled Demos", icon: <EventAvailable sx={{ fontSize: 20 }} />, path: "/super-admin/scheduled-demos" },
        { text: "All Support Tickets", icon: <ConfirmationNumber sx={{ fontSize: 20 }} />, path: "/allsupporttickets" },
      ],
    },
    {
      section: "Financial",
      items: [
        { text: "All Payments History", icon: <Payment sx={{ fontSize: 20 }} />, path: "/super-admin/payments-history" },
        { text: "All Subscription History", icon: <Subscriptions sx={{ fontSize: 20 }} />, path: "/super-admin/allsubscriptions" },
        { text: "Custom Pricing", icon: <Payment sx={{ fontSize: 20 }} />, path: "/super-admin/custom-pricing" },
      ],
    },
  ],

  admin: [
    {
      section: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, path: "/admin/dashboard" },
      ],
    },
    {
      section: "Recruitment",
      items: [
        { text: "Jobs Posted", icon: <Work sx={{ fontSize: 20 }} />, path: "/admin/jobs" },
        { text: "Applications", icon: <Assignment sx={{ fontSize: 20 }} />, path: "/recruiter-applications" },
        { text: "Interviews", icon: <History sx={{ fontSize: 20 }} />, path: "/job-interview-history" },
        { text: "Direct Interviews", icon: <VideoCall sx={{ fontSize: 20 }} />, path: "/direct-interviews" },
        { text: "Interview Recordings", icon: <VideoLibrary sx={{ fontSize: 20 }} />, path: "/recruiter/interview-recordings" },
        { text: "Technologies", icon: <Category sx={{ fontSize: 20 }} />, path: "/admin/technologies" },
        { text: "Offer Letter Templates", icon: <Drafts sx={{ fontSize: 20 }} />, path: "/admin/offer-letters" },
      ],
    },
    {
      section: "Communication Center",
      items: [
        { text: "Dashboard", icon: <Dashboard sx={{ fontSize: 20 }} />, path: "/communication-center" },
        { text: "Calling Templates", icon: <RecordVoiceOver sx={{ fontSize: 20 }} />, path: "/communication-center/voice-templates" },
        { text: "Candidate Outreach", icon: <Phone sx={{ fontSize: 20 }} />, path: "/communication-center/ai-calling" },
        { text: "Credit Usage", icon: <Assessment sx={{ fontSize: 20 }} />, path: "/communication-center/usage" },
        { text: "Purchase History", icon: <History sx={{ fontSize: 20 }} />, path: "/communication-center/purchases" },
        { text: "Credit Ledger", icon: <ReceiptLong sx={{ fontSize: 20 }} />, path: "/communication-center/ledger" },
      ],
    },
    {
      section: "Financial",
      items: [
        { text: "Payments", icon: <Payment sx={{ fontSize: 20 }} />, path: "/payment" },
        { text: "Subscription", icon: <Subscriptions sx={{ fontSize: 20 }} />, path: "/subscription" },
      ],
    },
    {
      section: "Support",
      items: [
        { text: "Support", icon: <SupportAgent sx={{ fontSize: 20 }} />, path: "/support" },
      ],
    },
  ],

  candidate: [
    {
      section: "Overview",
      items: [
        { text: "Dashboard", icon: <Dashboard />, path: "/candidate/dashboard" },
      ],
    },
    {
      section: "Career",
      items: [
        { text: "My Applications", icon: <Assignment />, path: "/my-applications" },
        { text: "Interview History", icon: <History />, path: "/my-interview-history" },
        { text: "Saved Jobs", icon: <BookmarkBorder />, path: "/saved-jobs",
        },
      ],
    },
    {
      section: "Support",
      items: [
        { text: "Support", icon: <SupportAgent />, path: "/support" },
      ],
    },
  ],
};

function Sidebar({ mobileOpen, handleDrawerToggle }) {
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  // Responsive sidebar width: narrower on smaller screens, full 240px on xl+
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));   // >= 1600px
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));   // >= 1366px
  const drawerWidth = isXlUp ? 240 : isLgUp ? 220 : 200;

  const [stats, setStats] = useState({
    profileCompletion: 0,
    applications: 0,
    interviews: 0,
    completed: 0,
    upcoming: 0,
    profileImage: null
  });
  const navigate = useNavigate();
  const location = useLocation();

  const role = String(user?.role || '').trim().toLowerCase();

  const hasAnalyticsAccess =
      user?.plan === 'professional' ||
      user?.plan === 'annual' ||
      user?.plan === 'monthly' ||
      user?.plan === 'custom';


  const buildAdminSections = () => {
    const base = menuConfig['admin'] || [];
    if (!hasAnalyticsAccess) return base;

    return base.map(section => {
      if (section.section === 'Recruitment') {
        const items = [...section.items];
        const interviewIdx = items.findIndex(i => i.text === 'Direct Interviews');
        const analyticsItem = {
          text: 'Analytics',
          icon: <BarChart3 size={20} />,
          path: '/analytics',
          badge: user?.plan === 'professional' || user?.plan === 'annual' ? 'PRO' : null,
        };
        if (interviewIdx >= 0) {
          items.splice(interviewIdx + 1, 0, analyticsItem);
        } else {
          items.push(analyticsItem);
        }
        return { ...section, items };
      }
      return section;
    });
  };

  const sections = role === 'admin' ? buildAdminSections() : (menuConfig[role] || []);

  const calculateProfileCompletion = (userData) => {
    const isFilled = (value) =>
        String(value || "").trim().length > 0;

    const scoreSection = (values, weight) => {
      if (!values.length) return 0;
      const filled = values.filter(isFilled).length;
      return Math.round((filled / values.length) * weight);
    };

    let score = 0;

    const personalDetails =
        typeof userData.personal_details === "string"
            ? JSON.parse(userData.personal_details || "{}")
            : (userData.personal_details || {});

    const education =
        typeof userData.education === "string"
            ? JSON.parse(userData.education || "[]")
            : (userData.education || []);

    const experience =
        typeof userData.experience_list === "string"
            ? JSON.parse(userData.experience_list || "[]")
            : (userData.experience_list || []);

    const projects =
        typeof userData.projects === "string"
            ? JSON.parse(userData.projects || "[]")
            : (userData.projects || []);

    const certifications =
        typeof userData.certifications === "string"
            ? JSON.parse(userData.certifications || "[]")
            : (userData.certifications || []);

    score += scoreSection(
        [userData.first_name, userData.last_name, userData.phone],
        15
    );
    score += scoreSection([userData.profile_summary], 10);
    score += scoreSection(
        [
          personalDetails.gender,
          personalDetails.maritalStatus,
          personalDetails.dateOfBirth,
          personalDetails.address,
          personalDetails.workPermit,
          personalDetails.category,
          personalDetails.passedOutYear
        ],
        15
    );

    if (education.length > 0) {
      const edu = education[0];
      score += scoreSection(
          [
            edu.education,
            edu.university,
            edu.course,
            edu.specialization,
            edu.courseType,
            edu.startYear,
            edu.endYear,
            edu.gradingSystem,
            edu.gradingValue
          ],
          15
      );
    }

    score += scoreSection([userData.skills], 10);

    if (experience.length > 0) {
      const exp = experience[0];
      score += scoreSection(
          [
            exp.currentEmployment,
            exp.employmentType,
            exp.totalExperienceYears,
            exp.totalExperienceMonths,
            exp.currentCompanyName,
            exp.currentJobTitle,
            exp.joiningYear,
            exp.joiningMonth,
            exp.currentSalary,
            exp.skillsUsed,
            exp.jobProfile,
            exp.noticePeriod
          ],
          15
      );
    }

    if (projects.length > 0) {
      const p = projects[0];
      score += scoreSection(
          [p.projectName, p.role, p.techStack, p.duration, p.description],
          10
      );
    }

    if (certifications.length > 0) {
      const c = certifications[0];
      score += scoreSection(
          [c.certificationName, c.issuedBy, c.certificationId],
          10
      );
    }

    score += scoreSection([userData.linkedin_url, userData.github_url], 10);

    return Math.min(100, score);
  };

  useEffect(() => {
    if (role !== "candidate") return;

    const loadSidebarStats = async () => {
      try {
        const [applicationsRes, profileRes] =
            await Promise.all([
              api.get("/applications/candidate/applications"),
              api.get("/auth/user")
            ]);
        const applications = Array.isArray(applicationsRes.data?.applications)
            ? applicationsRes.data.applications
            : Array.isArray(applicationsRes.data)
                ? applicationsRes.data
                : [];
        const interviewCount = applications.filter(app =>
            app.status === "ai_coding_scheduled" ||
            app.status === "ai_technical_interview_scheduled" ||
            app.status === "ai_hr_interview_scheduled" ||
            app.status === "technical_scheduled" ||
            app.status === "technical_l2_interview_scheduled" ||
            app.status === "hr_scheduled" ||
            app.status === "hr_l2_interview_scheduled"
        ).length;

        const userData = profileRes.data;
        const score = calculateProfileCompletion(userData);

        const upcomingCount = applications.filter(
            app => app.status === "application_shortlisted"
        ).length;

        const completedCount = applications.filter(app =>
            app.status === "ai_technical_interview_completed" ||
            app.status === "ai_hr_interview_completed" ||
            app.status === "technical_completed" ||
            app.status === "technical_l2_interview_completed" ||
            app.status === "hr_completed" ||
            app.status === "hr_l2_interview_completed"
        ).length;

        setStats({
          profileCompletion: score,
          applications: applications.length,
          interviews: interviewCount,
          completed: completedCount,
          upcoming: upcomingCount,
          profileImage: userData?.profile_image_url
        });
      } catch (err) {
        console.error("Sidebar stats error:", err);
      }
    };
    loadSidebarStats();
  }, [role]);

  if (!isAuthenticated) return null;

  const drawerContent = (
      <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            px: 1.5,
            pb: 3,
            '&::-webkit-scrollbar': { width: '3px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: SB.scrollThumb,
              borderRadius: '2px',
            },
          }}
      >
        {sections.map((section, index) => (
            <Box key={section.section} sx={{ mt: index === 0 ? 2 : 1.5 }}>
              {/* Section heading */}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, mb: 0.5 }}>
                <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.72)",   // Increased contrast
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                      letterSpacing: "0.12em",
                    }}
                >
                  {section.section}
                </Typography>
                {section.badge && (
                    <Chip
                        label={section.badge}
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor: SB.activeBg,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.55rem',
                          height: 14,
                          borderRadius: '4px',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                    />
                )}
              </Box>

              {/* Menu items */}
              <List sx={{ mt: 0, p: 0 }}>
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                      <ListItemButton
                          key={item.text}
                          onClick={() => {
                            navigate(item.path);
                            if (handleDrawerToggle) handleDrawerToggle();
                          }}
                          selected={isActive}
                          sx={{
                            borderRadius: '10px',
                            mx: 0,
                            mb: 0.25,
                            py: 0.9,
                            px: 1.5,
                            transition: 'background 0.18s ease, transform 0.18s ease',

                            // Default state
                            bgcolor: 'transparent',
                            '& .MuiListItemIcon-root': { color: SB.iconDefault },

                            // Hover
                            '&:hover': {
                              bgcolor: SB.hoverBg,
                              transform: 'translateX(2px)',
                              '& .MuiListItemIcon-root': { color: SB.iconHover },
                              '& .MuiListItemText-primary': { color: '#ffffff' },
                            },

                            // Active
                            '&.Mui-selected': {
                              bgcolor: SB.activeBg,
                              boxShadow: SB.activeGlow,
                              '& .MuiListItemIcon-root': { color: SB.iconActive },
                              '& .MuiListItemText-primary': {
                                color: SB.textActive,
                                fontWeight: 700,
                              },
                              '&:hover': {
                                bgcolor: '#1D4ED8',
                                transform: 'translateX(2px)',
                              },
                            },
                          }}
                      >
                        <ListItemIcon
                            sx={{
                              minWidth: 36,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: isActive ? 700 : 500,
                              color: isActive ? SB.textActive : SB.textDefault,
                              lineHeight: 1.4,
                              noWrap: true,
                            }}
                        />
                        {item.badge && (
                            <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(37,99,235,0.55)',
                                  color: '#fff',
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  height: 18,
                                  borderRadius: '999px',
                                  '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        )}
                      </ListItemButton>
                  );
                })}
              </List>

              {index !== sections.length - 1 && (
                  <Divider sx={{ my: 1.25, borderColor: SB.divider }} />
              )}
            </Box>
        ))}

        {/* Candidate profile card */}
        {role === "candidate" && (
            <Box
                sx={{
                  mx: 0,
                  mt: 2.5,
                  p: 2,
                  borderRadius: '14px',
                  background: SB.cardBg,
                  border: `1px solid ${SB.cardBorder}`,
                }}
            >
              <Box textAlign="center">
                <Avatar
                    src={stats.profileImage || user?.profile_image_url}
                    sx={{
                      width: 56,
                      height: 56,
                      mx: "auto",
                      mb: 1.5,
                      border: '2px solid rgba(255,255,255,0.18)',
                    }}
                />

                <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: '#ffffff' }}>
                  {user?.first_name} {user?.last_name}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 0.25 }}
                >
                  Profile Completion
                </Typography>

                <LinearProgress
                    variant="determinate"
                    value={stats.profileCompletion}
                    sx={{
                      mt: 1,
                      mb: 0.5,
                      height: 6,
                      borderRadius: 10,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: SB.activeBg,
                        borderRadius: 10,
                      },
                    }}
                />

                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  {stats.profileCompletion}% Complete
                </Typography>

                <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      mt: 1.5,
                      bgcolor: SB.activeBg,
                      '&:hover': { bgcolor: '#1D4ED8' },
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      fontSize: '0.8rem',
                      boxShadow: 'none',
                      height: 36,
                    }}
                    onClick={() => navigate("/profile")}
                >
                  Update Profile
                </Button>

                <Box mt={2}>
                  <Typography
                      fontWeight={700}
                      fontSize="0.78rem"
                      mb={1}
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    Your Activity
                  </Typography>

                  <Grid container spacing={1}>
                    {[
                      { label: 'Applications', value: stats.applications },
                      { label: 'Interviews',   value: stats.interviews },
                      { label: 'Completed',    value: stats.completed },
                      { label: 'Upcoming',     value: stats.upcoming },
                    ].map(({ label, value }) => (
                        <Grid item xs={6} key={label}>
                          <Box
                              sx={{
                                p: 1,
                                textAlign: 'center',
                                borderRadius: '10px',
                                background: SB.statBg,
                                border: `1px solid ${SB.cardBorder}`,
                              }}
                          >
                            <Typography fontWeight={700} sx={{ color: '#ffffff', fontSize: '1.05rem' }}>
                              {value}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem' }}
                            >
                              {label}
                            </Typography>
                          </Box>
                        </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Box>
        )}
      </Box>
  );

  // Shared paper styles for both mobile and desktop drawers
  const paperStyles = {
    bgcolor: SB.bg,
    borderRight: `1px solid ${SB.divider}`,
    boxShadow: 'none',
  };

  return (
      <>
        {/* Mobile Drawer */}
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                ...paperStyles,
                boxSizing: "border-box",
                width: drawerWidth,
                top: 0,
                height: "100%",
              },
            }}
        >
          <Box
              sx={{
                py: 2,
                px: 2,
                display: "flex",
                alignItems: "center",
                borderBottom: `1px solid ${SB.divider}`,
              }}
          >
            <img src="/logo-new.png?v=3" alt="ExiHire Logo" style={{ height: 50 }} />
          </Box>
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                ...paperStyles,
                width: drawerWidth,
                boxSizing: "border-box",
                position: "sticky",
                top: "64px",
                height: "calc(100vh - 64px)",
                zIndex: (theme) => theme.zIndex.appBar - 1,
              },
            }}
        >
          {drawerContent}
        </Drawer>
      </>
  );
}

export default Sidebar;
